import {
  Component,
  computed,
  forwardRef,
  HostBinding,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
  DOCUMENT,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Localization, LOCALIZATIONS } from '@cue/translate';
import { SwUpdate } from '@angular/service-worker';
import {
  DropdownlistComponent,
  DropdownsItemTemplateDirective,
  ValueTemplateDirective,
} from '@cue/assist/ui/dropdowns';
import { RouterOutlet, ActivatedRoute } from '@angular/router';
import { FaviconService, RtlDirective, RtlProvider } from '@cue/utilities';
import { NavigationConfigService } from './config.service';
import { MessageService } from '@progress/kendo-angular-l10n';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { SignalRService } from '@cue/api';

@Component({
  selector: 'nav-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    DropdownlistComponent,
    ValueTemplateDirective,
    DropdownsItemTemplateDirective,
    RouterOutlet,
    ReactiveFormsModule,
    RtlDirective,
    TranslocoPipe,
  ],
  providers: [
    {
      provide: RtlProvider,
      useExisting: forwardRef(() => AppComponent),
    },
  ],
})
export class AppComponent extends RtlProvider implements OnInit {
  signalRService = inject(SignalRService);
  private swUpdate = inject(SwUpdate);
  public translocoService = inject(TranslocoService);
  private route = inject(ActivatedRoute);
  private localizations = inject<Localization[][]>(LOCALIZATIONS, { optional: true });
  protected showLanguageSelector: boolean;
  @HostBinding('class') classes = '';
  public languageItems: Array<{ text: string; flag: string }> = [
    // TOUCH-240 - vypnuti jazyku dokud nejsou spravne preklady
    //{ text: "AR", flag: "ar.svg" },
    { text: 'CS', flag: 'cs.svg' },
    //{ text: "DE", flag: "ge.svg"},
    { text: 'EN', flag: 'en.svg' },
    { text: 'ES', flag: 'es.svg' },
    { text: 'DE', flag: 'ge.svg' },
    { text: 'CA', flag: 'ca.svg' },
    { text: 'UA', flag: 'ua.svg' },
    { text: 'FR', flag: 'fr.svg' },
    { text: 'AR', flag: 'ar.svg' },
    // { text: "NB", flag: "ne.svg"},
    { text: 'PL', flag: 'pl.svg' },
  ].filter(
    (lang) => this.translocoService.getAvailableLangs().find((l) => l === lang.text.toLowerCase()) !== undefined
  );
  currentLanguageControl = new FormControl<{ text: string; flag: string }>(null);
  title = 'navigation';

  mapOnly = injectQueryParams('mapOnly', {
    transform: (value: string | boolean) => {
      if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
      }
      return !!value;
    },
  });
  #messageService = inject(MessageService);
  #rtl = signal(false);
  override rtl = this.#rtl.asReadonly();
  public rtlLanguages: string[] = ['ar', 'he', 'fa', 'ur'];

  configService = inject(NavigationConfigService);
  faviconService = inject(FaviconService);
  document = inject(DOCUMENT);

  themeVariables = computed(() => {
    const theme = this.configService.value.theme;
    return {
      '--navigation-theme-primary': theme?.primary,
      '--navigation-theme-primary-text': theme?.primaryText,
      '--navigation-theme-secondary-text': theme?.secondaryText,
      '--navigation-theme-info': theme?.info,
      '--navigation-theme-success': theme?.success,
      '--navigation-theme-warning': theme?.warning,
      '--navigation-theme-error': theme?.error,
    };
  });

  appendVariablesToBody() {
    const setVariableKeys = Object.keys(this.themeVariables()).filter((key) => this.themeVariables()[key]);
    if (setVariableKeys.length === 0) return;
    const code = setVariableKeys.reduce((acc, it) => {
      return acc + `${it}: ${this.themeVariables()[it]};\n`;
    }, '');

    const style = this.document.createElement('style');
    style.type = 'text/css';
    style.innerHTML = `
      :root {
        ${code}
      }
    `;
    this.document.head.appendChild(style);
  }

  get currentLanguage(): { text: string; flag: string } {
    const availableLangs: any[] = this.translocoService.getAvailableLangs();
    const langFromLocalStorage = localStorage.getItem('assist-lang');
    if (langFromLocalStorage != undefined) {
      const found = availableLangs.find((x) => x == langFromLocalStorage);
      if (found) {
        const isRtl = this.rtlLanguages.includes(found);
        this.#rtl.set(isRtl);
        this.#messageService.notify(isRtl);
      }
      return this.languageItems.find((x) => x.text == (found ?? this.translocoService.getDefaultLang()).toUpperCase())!;
    } else {
      const found = availableLangs.find((x) => x == navigator.language.slice(0, 2));
      if (found) {
        const isRtl = this.rtlLanguages.includes(found);
        this.#rtl.set(isRtl);
        this.#messageService.notify(isRtl);
      }
      return this.languageItems.find((x) => x.text == (found ?? this.translocoService.getDefaultLang()).toUpperCase())!;
    }
  }

  set currentLanguage(value: { text: string; flag: string }) {
    const availableLangs: any[] = this.translocoService.getAvailableLangs();
    const found = availableLangs.find((x) => x == value.text.toLowerCase());

    if (found) {
      const translation = this.localizations[0].find((x) => x.language == found)!;
      translation.valueGetter().then((x) => {
        localStorage.setItem('assist-lang', found);
        this.translocoService.setTranslation(x, translation.language, {
          merge: false,
        });
        const isRtl = this.rtlLanguages.includes(found);
        this.#rtl.set(isRtl);
        this.#messageService.notify(isRtl);
        this.translocoService.setActiveLang(found);
      });
    }
  }

  ngOnInit() {
    this.signalRService.startConnection('/MessageHub');

    this.currentLanguageControl.setValue(this.currentLanguage, {
      emitEvent: false,
    });
    this.currentLanguageControl.valueChanges.subscribe((val) => {
      this.currentLanguage = val;
    });
    this.route.queryParams.subscribe((params) => {
      this.classes = params['kioskId'] ? 'kiosk-mode' : '';
    });
    this.appendVariablesToBody();
    if (this.configService.value.favIconUrl) {
      this.faviconService.setFavicon(this.configService.value.favIconUrl);
    }

    this.translocoService.langChanges$.subscribe((x) => {
      this.currentLanguageControl.setValue(this.currentLanguage, {
        emitEvent: false,
      });
    });

    this.showLanguageSelector = this.configService.value.languages.filter((x) => x.activeInNavigation).length > 1;

    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe((evt) => {
        switch (evt.type) {
          case 'VERSION_DETECTED':
            console.log(`Downloading new app version: ${evt.version.hash}`);
            break;
          case 'VERSION_READY':
            // if (confirm('New version available. Load New Version?')) {
            //   window.location.reload();
            // }
            window.location.reload();
            break;
          case 'VERSION_INSTALLATION_FAILED':
            console.log(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
            break;
        }
      });
    }
  }
}
